import React, { useState, useEffect } from "react";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { Checkbox } from 'primereact/checkbox';
import { InputSwitch } from "primereact/inputswitch";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toggleProductStatus, toggleFrontpageStatus, getProducts, /* updateProductQuantity, */ deactiveMultipleProduct, deleteMultipleProduct } from "../../store/AsyncMethods/ProductMethod";
import { getCategory } from "../../store/AsyncMethods/CategoryMethod";
import { saveAs } from "file-saver";

const categories = [
    { label: 'Electronics', value: 'electronics' },
    { label: 'Fashion', value: 'fashion' },
    { label: 'Home & Kitchen', value: 'home' },
];

const sortOptions = [
    { label: 'Price: Low to High', value: 'price_asc' },
    { label: 'Price: High to Low', value: 'price_desc' },
    { label: 'Newest First', value: 'date_desc' },
];

export default function ProductTable({ products = [] }) {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const categories = useSelector(state => state.CategoryReducer.categories);
    const { enableCurrency } = useSelector(state => state.CurrencyReducer);
    const [activeTab, setActiveTab] = useState('Active');
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(20); // Changed from hardcoded to state
    const [expandedProductIds, setExpandedProductIds] = useState([]);
    const [outOfStockOnly, setOutOfStockOnly] = useState(false);
    const [selectedCategoryId, setSelectedCategoryId] = useState(null);
    const [selectedSortOption, setSelectedSortOption] = useState(null);
    const [filterType, setFilterType] = useState('title');
    const [filterText, setFilterText] = useState('');
    // Stock editing functionality commented out
    // const [editingStockProductId, setEditingStockProductId] = useState(null);
    // const [variantQuantities, setVariantQuantities] = useState({});

    useEffect(() => {
        dispatch(getCategory());
    }, [dispatch]);

    const onProductSelect = (e, product) => {
        let _selectedProducts = [...selectedProducts];
        if (e.checked)
            _selectedProducts.push(product);
        else
            _selectedProducts = _selectedProducts.filter(p => p.id !== product.id);

        setSelectedProducts(_selectedProducts);
    }

    const handleEditProduct = (productId) => {
        console.log("productId", productId);
        navigate(`/edit-product/${productId}`);
    };

    // Filter products based on active tab and out of stock
    const getFilteredProducts = () => {
        let filtered = [];
        switch (activeTab) {
            case 'Active':
                filtered = products.filter(product => product.is_active === 1 && product.is_deleted !== 1);
                break;
            case 'Inactive':
                filtered = products.filter(product => product.is_active === 0 && product.is_deleted !== 1);
                break;
            case 'Deleted':
                filtered = products.filter(product => product.is_deleted === 1);
                break;
            case 'All':
                filtered = products.filter(product => product.is_deleted !== 1);
                break;
            case 'Draft':
                filtered = products.filter(product => product.is_draft === 1 && product.is_deleted !== 1);
                break;
            case 'Pending QC':
                filtered = [];
                break;
            case 'Violation':
                filtered = [];
                break;
            default:
                filtered = products;
        }
        // Apply Product Name or Seller Sku filter
        if (filterText && filterType === 'title') {
            filtered = filtered.filter(product =>
                product.title && product.title.toLowerCase().includes(filterText.toLowerCase())
            );
        } else if (filterText && filterType === 'id') {
            filtered = filtered.filter(product =>
                product.id && String(product.id).toLowerCase().includes(filterText.toLowerCase())
            );
        }
        if (selectedCategoryId) {
            filtered = filtered.filter(product => product.category_id === selectedCategoryId);
        }
        if (outOfStockOnly) {
            filtered = filtered.filter(product => {
                // If product has variants, sum their quantities
                if (product.varients && product.varients.length > 0) {
                    const totalQty = product.varients.reduce((sum, v) => sum + (v.quantity || 0), 0);
                    return totalQty === 0;
                }
                // Otherwise, use product stock
                return (product.varients?.[0]?.quantity || product.stock || 0) === 0;
            });
        }
        if (selectedSortOption) {
            if (selectedSortOption === 'price_asc') {
                filtered = [...filtered].sort((a, b) => (a.price || 0) - (b.price || 0));
            } else if (selectedSortOption === 'price_desc') {
                filtered = [...filtered].sort((a, b) => (b.price || 0) - (a.price || 0));
            } else if (selectedSortOption === 'date_desc') {
                filtered = [...filtered].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
            }
        }
        return filtered;
    };

    const filteredProducts = getFilteredProducts();

    // Calculate pagination
    const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentProducts = filteredProducts.slice(startIndex, endIndex);

    const handlePageChange = (page) => {
        setCurrentPage(page);
        setSelectedProducts([]); // Clear selection when changing pages
    };

    const handleItemsPerPageChange = (newItemsPerPage) => {
        setItemsPerPage(newItemsPerPage);
        setCurrentPage(1); // Reset to first page when changing items per page
        setSelectedProducts([]); // Clear selection
    };

    const handleTabChange = (tab) => {
        setActiveTab(tab);
        setCurrentPage(1); // Reset to first page when changing tabs
        setSelectedProducts([]); // Clear selection when changing tabs
    };

    const handlePreviousPage = () => {
        if (currentPage > 1) {
            handlePageChange(currentPage - 1);
        }
    };

    const handleNextPage = () => {
        if (currentPage < totalPages) {
            handlePageChange(currentPage + 1);
        }
    };

    // Generate page numbers for pagination
    const getPageNumbers = () => {
        const pages = [];
        const maxVisiblePages = 5;
        
        if (totalPages <= maxVisiblePages) {
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
        } else {
            if (currentPage <= 3) {
                for (let i = 1; i <= 4; i++) {
                    pages.push(i);
                }
                pages.push('...');
                pages.push(totalPages);
            } else if (currentPage >= totalPages - 2) {
                pages.push(1);
                pages.push('...');
                for (let i = totalPages - 3; i <= totalPages; i++) {
                    pages.push(i);
                }
            } else {
                pages.push(1);
                pages.push('...');
                for (let i = currentPage - 1; i <= currentPage + 1; i++) {
                    pages.push(i);
                }
                pages.push('...');
                pages.push(totalPages);
            }
        }
        
        return pages;
    };
    
    const tabs = ['All', 'Active', 'Inactive', 'Draft', 'Pending QC', 'Violation', 'Deleted'];
    
    // Calculate actual counts for each tab
    const tabCounts = {
        'All': products.filter(product => product.is_deleted !== 1).length,
        'Active': products.filter(product => product.is_active === 1 && product.is_deleted !== 1).length,
        'Inactive': products.filter(product => product.is_active === 0 && product.is_deleted !== 1).length,
        'Deleted': products.filter(product => product.is_deleted === 1).length,
        'Draft': products.filter(product => product.is_draft === 1 && product.is_deleted !== 1).length,
        'Pending QC': products.filter(product => product.is_active === 1 && product.is_deleted !== 1 && product.is_qc_pending === 1).length,
        'Violation': products.filter(product => product.is_active === 1 && product.is_deleted !== 1 && product.is_qc_pending === 0).length,
    };

    function getContentScore(product) {
        let filled = 0;
        let total = 4; // title, price/varients, description, images

        // Basic Information
        if (product.title && product.title.trim() !== "") filled++;

        // Price, Stock & Variants
        if (product.price && product.varients && product.varients.length > 0) filled++;

        // Product Description
        if (product.description && product.description.trim() !== "") filled++;

        // Images
        if ((product.all_images && product.all_images.length > 0) || (product.image_path && product.image_path !== "")) filled++;

        const percent = Math.round((filled / total) * 100);
        let label = "Poor";
        let color = "text-red-500";
        if (percent === 100) {
            label = "Excellent";
            color = "text-green-500";
        } else if (percent >= 75) {
            label = "Good";
            color = "text-blue-500";
        } else if (percent >= 50) {
            label = "Qualified";
            color = "text-yellow-500";
        }
        return { label, color, percent };
    }

    const toggleExpand = (productId) => {
        setExpandedProductIds(prev =>
            prev.includes(productId)
                ? prev.filter(id => id !== productId)
                : [...prev, productId]
        );
    };

    const handleActiveToggle = async (productId) => {
        await dispatch(toggleProductStatus(productId));
        dispatch(getProducts());
    };

    const handleFrontpageToggle = async (e, productId) => {
        e.stopPropagation();
        await dispatch(toggleFrontpageStatus(productId));
        dispatch(getProducts());
    };

    // Function to calculate price with discount
    const calculatePrice = (basePrice, discount, discountType) => {
        if (!basePrice || !discount) return basePrice;
        
        if (discountType === 'percentage') {
            return basePrice - (basePrice * discount / 100);
        } else if (discountType === 'fixed') {
            return basePrice - discount;
        }
        return basePrice;
    };

    // Function to get product price display
    const getProductPriceDisplay = (product) => {
        // For parent product, use product-level price
        const basePrice = product.price || 0;
        const discount = product.discount || 0;
        const discountType = product.discount_type || 'fixed';
        
        const finalPrice = calculatePrice(basePrice, discount, discountType);
        
        return {
            finalPrice: finalPrice.toLocaleString(),
            originalPrice: basePrice.toLocaleString(),
            hasDiscount: discount > 0
        };
    };

    // Function to get variant price display
    const getVariantPriceDisplay = (variant, parentProduct) => {
        // For variants, use variant-level price if available, otherwise fall back to parent
        const basePrice = variant.price || parentProduct.price || 0;
        const discount = variant.discount || 0;
        const discountType = variant.discount_type || 'fixed';
        
        const finalPrice = calculatePrice(basePrice, discount, discountType);
        
        return {
            finalPrice: finalPrice.toLocaleString(),
            originalPrice: basePrice.toLocaleString(),
            hasDiscount: discount > 0
        };
    };

  return (
    <div className="bg-white rounded-lg shadow-md">
      {/* Tabs */}
      <div className="flex border-b">
        {tabs.map(tab => (
           <button 
             key={tab} 
             onClick={() => handleTabChange(tab)}
             className={`py-3 px-4 text-sm font-semibold ${activeTab === tab ? 'text-orange-500 border-b-2 border-orange-500' : 'text-gray-600'}`}
           >
             {tab} {tabCounts[tab] && <span className="ml-1 bg-orange-100 text-orange-600 text-xs font-bold px-2 py-0.5 rounded-full">{tabCounts[tab]}</span>}
           </button>
        ))}
      </div>
      
      {/* Filters */}
      <div className="p-4 bg-gray-50 rounded-xl mb-2">
        <div className="flex items-center gap-4 mb-2 justify-between">
          <div className="flex items-center gap-4">
            <span className="text-gray-700 font-medium">Filter Product:</span>
            <button
              className={`px-4 py-1 rounded-md text-sm font-semibold border transition ${outOfStockOnly ? 'bg-orange-100 text-orange-600 border-orange-400' : 'bg-gray-100 text-gray-700 border-gray-200'}`}
              onClick={() => setOutOfStockOnly(v => !v)}
              type="button"
            >
              Out Of Stock
            </button>
          </div>
          <Button
            label="Reset"
            className="p-primary-btn p-button-sm"
            onClick={() => {
              setFilterType('title');
              setFilterText('');
              setSelectedCategoryId(null);
              setSelectedSortOption(null);
              setOutOfStockOnly(false);
            }}
          />
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center bg-white rounded-md border border-gray-200 overflow-hidden" style={{ minWidth: 340 }}>
            <Dropdown
              value={filterType}
              options={[
                { label: 'Product Name', value: 'title' },
                { label: 'Seller Sku', value: 'id' }
              ]}
              onChange={e => setFilterType(e.value)}
              placeholder="Product Name"
              className="border-0 min-w-[130px]"
              style={{ border: 'none', borderRadius: 0 }}
            />
            <InputText
              value={filterText}
              onChange={e => setFilterText(e.target.value)}
              placeholder="Please Input"
              className="border-0 flex-1 min-w-[120px]"
              style={{ border: 'none', borderRadius: 0 }}
              onKeyDown={e => { if (e.key === 'Enter') e.preventDefault(); }}
            />
            <span className="px-3 text-gray-400"><i className="pi pi-search" /></span>
          </div>
          <div className="flex items-center bg-white rounded-md border border-gray-200 overflow-hidden" style={{ minWidth: 220 }}>
            <span className="px-3 text-gray-500 h-full flex items-center font-medium">Select Category</span>
            <Dropdown
              placeholder="Please Select"
              value={selectedCategoryId}
              options={categories?.map(cat => ({ label: cat.name, value: cat.id }))}
              onChange={e => setSelectedCategoryId(e.value)}
              className="border-0 min-w-[100px]"
              style={{ border: 'none', borderRadius: 0 }}
              showClear
            />
          </div>
          <div className="flex items-center bg-white rounded-md border border-gray-200 overflow-hidden" style={{ minWidth: 220 }}>
            <span className="px-3 text-gray-500 h-full flex items-center font-medium">Sort By</span>
            <Dropdown
              placeholder="Please Select"
              value={selectedSortOption}
              options={sortOptions}
              optionLabel="label"
              optionValue="value"
              onChange={e => setSelectedSortOption(e.value)}
              className="border-0 min-w-[100px]"
              style={{ border: 'none', borderRadius: 0 }}
              showClear
            />
          </div>
        </div>
      </div>

      {/* Bulk Actions */}
       <div className="p-4 flex items-center gap-4 text-sm bg-gray-100 border-y">
         <Checkbox
           checked={selectedProducts.length === filteredProducts.length && filteredProducts.length > 0}
           onChange={e => {
             if (e.checked) {
               setSelectedProducts(filteredProducts);
             } else {
               setSelectedProducts([]);
             }
           }}
           indeterminate={selectedProducts.length > 0 && selectedProducts.length < filteredProducts.length}
         />
         <span className="text-gray-500">{selectedProducts.length} products selected</span>
         <Button
           label="Deactivate"
           className="p-button-secondary p-primary-btn"
           disabled={selectedProducts.length === 0}
           onClick={async () => {
             await dispatch(deactiveMultipleProduct(selectedProducts.map(p => p.id)));
             setSelectedProducts([]);
             dispatch(getProducts());
           }}
         />
         <Button
           label="Delete"
           className="p-button-secondary p-primary-btn"
           disabled={selectedProducts.length === 0}
           onClick={async () => {
             await dispatch(deleteMultipleProduct(selectedProducts.map(p => p.id)));
             setSelectedProducts([]);
             dispatch(getProducts());
           }}
         />
         <Button
           label="Export Selected Products"
           className="p-button-secondary p-primary-btn"
           disabled={selectedProducts.length === 0}
           onClick={() => {
             const csvRows = [
               ["ID", "Title", "Stock"],
               ...selectedProducts.map(pRaw => {
                 // pRaw may be an id (number/string) or product-like object
                 const p = (typeof pRaw === 'number' || typeof pRaw === 'string') ? products.find(x => String(x.id) === String(pRaw)) || { id: pRaw } : pRaw || {};
                 const title = p.title || p.name || p.title_urdu || "";
                 let stock = 0;
                 if (p.varients && p.varients.length > 0) {
                   stock = p.varients.reduce((sum, v) => sum + (v.quantity || 0), 0);
                 } else {
                   stock = p.varients?.[0]?.quantity || p.stock || 0;
                 }
                 return [p.id, title, stock];
               })
             ];
             // escape values and wrap in double quotes to handle commas/newlines
             const csvContent = csvRows.map(row => row.map(cell => '"' + String(cell).replace(/"/g, '""') + '"').join(",")).join("\n");
             const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
             saveAs(blob, "selected_products.csv");
           }}
         />
         <Button
           label="Export Products Facebook Catalog CSV"
           className="p-button-secondary p-primary-btn"
           disabled={selectedProducts.length === 0}
           onClick={() => {
             const headers = [
               "id","title","description","availability","condition","price","link","image_link","brand","google_product_category","fb_product_category","quantity_to_sell_on_facebook","sale_price","sale_price_effective_date","item_group_id","gender","color","size","age_group","material","pattern","shipping","shipping_weight","offer_disclaimer","offer_disclaimer_url","video[0].url","video[0].tag[0]","gtin","product_tags[0]","product_tags[1]","style[0]"
             ];
             const rows = selectedProducts.map(pRaw => {
               const p = (typeof pRaw === 'number' || typeof pRaw === 'string') ? products.find(x => String(x.id) === String(pRaw)) || { id: pRaw } : pRaw || {};
               const totalStock = p.varients && p.varients.length > 0 ? p.varients.reduce((s,v)=>s+(v.quantity||0),0) : (p.varients?.[0]?.quantity || p.stock || 0);
               const priceNum = p.price || (p.varients && p.varients[0] && p.varients[0].price) || 0;
               const currency = enableCurrency?.unit || 'USD';
               const price = `${priceNum} ${currency}`;
               const salePrice = (p.discount && p.discount > 0) ? `${(priceNum - (p.discount_type === 'percentage' ? (priceNum * p.discount / 100) : p.discount))} ${currency}` : '';
               const image = p.image_path && p.all_images?.[0] ? `${p.image_path}${p.all_images[0].image}` : (p.image || '');
               const firstVariantId = p.varients?.[0]?.varient_id || p.varients?.[0]?.id || p.id;
               const link = `https://www.paradiseceramics.pk/product-details/${p.id}/${firstVariantId}`;
               return [
                 p.articleName || p.sku || p.id || '',
                 p.title || p.name || '',
                 (p.description || '').replace(/\r?\n/g, ' '),
                 totalStock > 0 ? 'in stock' : 'out of stock',
                 'new',
                 price,
                 link,
                 image,
                 p.brand || '',
                 '',
                 '',
                 totalStock,
                 salePrice,
                 (p.promoStartDate && p.promoEndDate) ? `${p.promoStartDate}/${p.promoEndDate}` : '',
                 p.articleName || '',
                 p.gender || '',
                 p.color || '',
                 p.size || '',
                 p.age_group || '',
                 p.material || '',
                 p.pattern || '',
                 '',
                 p.weight || '',
                 '',
                 '',
                 p.video || '',
                 p.video_tag || '',
                 p.gtin || '',
                 p.product_tags?.[0] || p.category_name || '',
                 p.product_tags?.[1] || '',
                 p.style || ''
               ];
             });
             const csvRows = [headers, ...rows];
             const csvContent = csvRows.map(row => row.map(cell => '"' + String(cell).replace(/"/g, '""') + '"').join(",")).join("\n");
             const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
             saveAs(blob, "facebook_product_feed.csv");
           }}
         />
      </div>

      {/* Table Header */}
       <div className="p-4 flex items-center text-sm font-bold text-gray-500 bg-gray-50">
          <div className="w-1/12">
            <Checkbox
              checked={currentProducts.length > 0 && currentProducts.every(p => selectedProducts.some(sel => sel.id === p.id))}
              indeterminate={selectedProducts.length > 0 && selectedProducts.length < currentProducts.length}
              onChange={e => {
                if (e.checked) {
                  // Add all current page products to selection (avoid duplicates)
                  const newSelection = [
                    ...selectedProducts.filter(sel => !currentProducts.some(p => p.id === sel.id)),
                    ...currentProducts
                  ];
                  setSelectedProducts(newSelection);
                } else {
                  // Remove all current page products from selection
                  setSelectedProducts(selectedProducts.filter(sel => !currentProducts.some(p => p.id === sel.id)));
                }
              }}
            />
          </div>
          <div className="w-3/12">Product Info</div>
          <div className="w-1/12">Price</div>
          <div className="w-1/12 text-center">Stock</div>
          <div className="w-1/12 text-center">Active</div>
          <div className="w-1/12 text-center">Frontpage</div>
          <div className="w-2/12">Content Score</div>
          <div className="w-2/12 text-right">Actions</div>
      </div>
      
      {/* Product List */}
      <div>
        {currentProducts.map((product, index) => {
          const score = getContentScore(product);
          const productPrice = getProductPriceDisplay(product);
          const mappedProduct = {
            id: product.id,
            image: product.image_path && product.all_images?.[0] ? `${product.image_path}${product.all_images[0].image}` : 'https://dummyimage.com/64x64/cccccc/000000&text=No+Image',
            name: product.title || 'Untitled Product',
            sellerSku: product.id || 'N/A',
            price: productPrice.finalPrice,
            originalPrice: productPrice.originalPrice,
            hasDiscount: productPrice.hasDiscount,
            stock: product.varients?.[0]?.quantity || 0,
            isActive: product.is_active === 1,
            isOnFrontpage: product.show_on_frontpage === 1,
            contentScore: score.label,
            contentScoreColor: score.color,
            contentScorePercent: score.percent
          };

          const isExpanded = expandedProductIds.includes(mappedProduct.id);

          return (
            <React.Fragment key={mappedProduct.id}>
              {/* Parent Product Row */}
              <div
                className={`p-4 flex items-center border-b cursor-pointer ${index % 2 !== 0 ? 'bg-white' : 'bg-white'}`}
                onClick={() => toggleExpand(mappedProduct.id)}
              >
                <div className="w-1/12 flex items-center">
                  <span className="mr-2">
                    <i className={`pi pi-chevron-${isExpanded ? 'down' : 'right'}`}></i>
                  </span>
                  <Checkbox onChange={e => onProductSelect(e, mappedProduct)} checked={selectedProducts.some(p => p.id === mappedProduct.id)}/>
                </div>
              <div className="w-3/12 flex items-center gap-3">
                <img src={mappedProduct.image} alt={mappedProduct.name} className="w-16 h-16 object-cover border rounded"/>
                <div>
                    <p className="text-sm text-gray-800 leading-tight">{mappedProduct.name}</p>
                    <p className="text-xs text-gray-500 mt-1">Seller Sku: {mappedProduct.sellerSku} <i className="pi pi-copy ml-1 cursor-pointer"></i></p>
                    <div className="flex gap-3 text-gray-400 mt-2">
                       <span title="The total units sold calculated based on the last 30 days. If 3 units of different SKU variation of the product are sold, it reflects 3.">
                         <i className="pi pi-box"></i>
                         <span className="ml-1 text-xs text-gray-700">{product?.sold_last_30_days ?? 0}</span>
                       </span>
                       <span title="The total number of views on product page in the last 30 days, regardless of SKU variations.">
                         <i className="pi pi-eye"></i>
                         <span className="ml-1 text-xs text-gray-700">{product?.views_last_30_days ?? 0}</span>
                       </span>
                       <span title="The total number of times this product was added to wishlists in the last 30 days.">
                         <i className="pi pi-heart"></i>
                         <span className="ml-1 text-xs text-gray-700">{product?.wishlists_last_30_days ?? 0}</span>
                       </span>
                       <span title="The average rating received in the last 30 days." className="flex items-center">
                         <i className="pi pi-star-fill text-yellow-400"></i>
                         <span className="text-xs ml-1">{product?.avg_rating_last_30_days ?? 0}</span>
                       </span>
                    </div>
                </div>
              </div>
              <div className="w-1/12 text-sm">
                  <p className="text-red-500 font-semibold">Rs. {mappedProduct.price}</p>
                    {mappedProduct.hasDiscount && (
                  <p className="text-gray-500 line-through">Rs. {mappedProduct.originalPrice}</p>
                    )}
                </div>
                <div className="w-1/12 text-center text-sm text-gray-700 flex justify-center items-center">
                  {/* Stock editing functionality commented out
                  {editingStockProductId === mappedProduct.id ? (
                    <>
                      {product.varients && product.varients.length > 0 ? (
                        <div className="flex flex-col gap-1">
                          {product.varients.map(variant => (
                            <div key={variant.varient_id} className="flex items-center gap-1">
                              <span className="text-xs text-gray-500 mr-1">{variant.name || variant.color || `Var ${variant.id}`}:</span>
                              <input
                                type="number"
                                className="border rounded px-1 w-14 text-center"
                                value={variantQuantities[variant.varient_id] ?? variant.quantity ?? 0}
                                onChange={e => setVariantQuantities(q => ({ ...q, [variant.varient_id]: Number(e.target.value) }))}
                                min={0}
                              />
                            </div>
                          ))}
                        </div>
                      ) : (
                        <input
                          type="number"
                          className="border rounded px-1 w-14 text-center"
                          value={variantQuantities['single'] ?? mappedProduct.stock}
                          onChange={e => setVariantQuantities(q => ({ ...q, single: Number(e.target.value) }))}
                          min={0}
                        />
                      )}
                      <i
                        className="pi pi-check ml-2 text-green-600 cursor-pointer"
                        onClick={async e => {
                          e.stopPropagation();
                          let payload;
                          if (product.varients && product.varients.length > 0) {
                            payload = {
                              varients: product.varients.map(v => ({ varient_id: v.varient_id, quantity: variantQuantities[v.varient_id] ?? v.quantity ?? 0 }))
                            };
                          } else {
                            payload = {
                              varients: [{ varient_id: product.id, quantity: variantQuantities['single'] ?? mappedProduct.stock }]
                            };
                          }
                          await dispatch(updateProductQuantity(payload));
                          setEditingStockProductId(null);
                          setVariantQuantities({});
                          dispatch(getProducts());
                        }}
                        title="Confirm"
                      />
                    </>
                  ) : (
                    <>
                      {mappedProduct.stock}
                      <i
                        className="pi pi-pencil ml-2 cursor-pointer"
                        onClick={e => {
                          e.stopPropagation();
                          setEditingStockProductId(mappedProduct.id);
                          if (product.varients && product.varients.length > 0) {
                            const initial = {};
                            product.varients.forEach(v => { initial[v.varient_id] = v.quantity ?? 0; });
                            setVariantQuantities(initial);
                          } else {
                            setVariantQuantities({ single: mappedProduct.stock });
                          }
                        }}
                        title="Edit Stock"
                      />
                    </>
                  )}
                  */}
                  {mappedProduct.stock}
                </div>
                <div className="w-1/12 flex justify-center">
                  <InputSwitch checked={mappedProduct.isActive} onChange={() => handleActiveToggle(mappedProduct.id)} />
              </div>
              <div className="w-1/12 flex flex-col items-center gap-1">
                  <InputSwitch
                    checked={mappedProduct.isOnFrontpage}
                    onChange={(e) => handleFrontpageToggle(e, mappedProduct.id)}
                  />
                  <span className="text-xs text-gray-400">
                    {mappedProduct.isOnFrontpage ? 'On' : 'Off'}
                  </span>
              </div>
              <div className="w-2/12 text-sm flex items-center">
                  <span className={`h-2 w-2 rounded-full mr-2 ${mappedProduct.contentScoreColor.replace('text-','bg-')}`}></span> 
                    {mappedProduct.contentScore} <span className="ml-2 text-xs text-gray-400">({mappedProduct.contentScorePercent}%)</span>
                  {mappedProduct.contentScore === 'Qualified' && <i className="pi pi-info-circle ml-1 text-gray-400"></i>}
              </div>
              <div className="w-2/12 text-right text-sm">
                  <button 
                    className="text-blue-600 hover:underline bg-transparent border-none cursor-pointer p-0"
                    onClick={e => { 
                      e.stopPropagation(); 
                      handleEditProduct(mappedProduct.id); 
                    }}
                  >
                    Edit More <i className="pi pi-angle-down text-xs"></i>
                  </button>
                </div>
              </div>
              {/* Variant Rows (only if expanded) */}
              {isExpanded && product.varients && product.varients.length > 0 && product.varients.map((variant, vIdx) => {
                // Fallbacks for variant fields
                const variantImage = variant.image || mappedProduct.image;
                const variantName = variant.name || variant.color || `Variant ${vIdx + 1}`;
                const variantSku = variant.sku || variant.sellerSku || `${mappedProduct.sellerSku}-${vIdx}`;
                const variantPrice = getVariantPriceDisplay(variant, product);
                const variantQuantity = variant.quantity || 0;
                const variantIsActive = variant.isActive !== undefined ? variant.isActive : mappedProduct.isActive;
                return (
                  <div key={variant.varient_id || vIdx} className="pl-20 flex items-center border-b bg-gray-50">
                    <div className="w-1/12"></div>
                    <div className="w-4/12 flex items-center gap-3">
                      <img src={variantImage} alt={variantName} className="w-12 h-12 object-cover border rounded"/>
                      <div>
                        <p className="text-sm text-gray-800">{variantName}</p>
                        <p className="text-xs text-gray-500 mt-1">Seller Sku: {variantSku}</p>
                      </div>
                    </div>
                    <div className="w-1/12 text-sm">
                      <p className="text-red-500 font-semibold">Rs. {variantPrice.finalPrice}</p>
                      {variantPrice.hasDiscount && (
                        <p className="text-gray-500 line-through">Rs. {variantPrice.originalPrice}</p>
                      )}
                    </div>
                    <div className="w-1/12 text-center text-sm text-gray-700 flex justify-center items-center">{variantQuantity}</div>
                    <div className="w-1/12 flex justify-center"></div>
                    <div className="w-2/12"></div>
                    <div className="w-2/12"></div>
            </div>
                );
              })}
            </React.Fragment>
          );
        })}
      </div>

      {/* Pagination */}
      <div className="p-4 bg-gray-50 flex items-center justify-between text-sm">
        <div className="flex items-center gap-4">
          {/* Items per page dropdown */}
          <div className="flex items-center gap-2">
            <Dropdown
              value={itemsPerPage}
              options={[
                { label: '10 / page', value: 10 },
                { label: '20 / page', value: 20 },
                { label: '50 / page', value: 50 },
                { label: '100 / page', value: 100 }
              ]}
              onChange={(e) => handleItemsPerPageChange(e.value)}
              className="w-32"
            />
          </div>
          
          {/* Total items count */}
          <div className="text-gray-600">
            Total {filteredProducts.length} items
          </div>
        </div>
        
        {/* Navigation controls - only show when there are multiple pages */}
        {totalPages > 1 && (
          <div className="flex items-center gap-1">
            <Button 
              icon="pi pi-chevron-left" 
              className="p-button-secondary p-button-sm" 
              onClick={handlePreviousPage} 
              disabled={currentPage === 1}
              style={{ 
                backgroundColor: currentPage === 1 ? '#f3f4f6' : '#ffffff',
                border: '1px solid #d1d5db',
                color: currentPage === 1 ? '#9ca3af' : '#374151',
                minWidth: '32px',
                height: '32px'
              }}
            />
            
            {getPageNumbers().map((page, index) => (
              <React.Fragment key={index}>
                {page === '...' ? (
                  <span className="px-3 py-2 text-gray-500">...</span>
                ) : (
                  <Button 
                    label={page} 
                    className="p-button-sm"
                    onClick={() => handlePageChange(page)}
                    style={{ 
                      backgroundColor: currentPage === page ? '#3b82f6' : '#ffffff',
                      border: '1px solid #d1d5db',
                      color: currentPage === page ? '#ffffff' : '#374151',
                      minWidth: '32px',
                      height: '32px'
                    }}
                  />
                )}
              </React.Fragment>
            ))}
            
            <Button 
              icon="pi pi-chevron-right" 
              className="p-button-secondary p-button-sm" 
              onClick={handleNextPage} 
              disabled={currentPage === totalPages}
              style={{ 
                backgroundColor: currentPage === totalPages ? '#f3f4f6' : '#ffffff',
                border: '1px solid #d1d5db',
                color: currentPage === totalPages ? '#9ca3af' : '#374151',
                minWidth: '32px',
                height: '32px'
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
}